import { HEADING_ALIGNMENT_OPTIONS, TITLE_SIZE_OPTIONS } from '@/common/constants';
import { HeadingAlignment, TitleSize } from '@/common/types';
import { CTAProps } from '@/components/CTA';
import classNames from 'classnames';
import React from 'react';
import { CTABlock } from '../CTABlock';

import { GradientTypography } from '../../GradientTypography';
import styles from './HeadingBlock.module.scss';

type TextElementType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';

const TextElement = ({
  type,
  text,
  className,
}: {
  type: TextElementType;
  text: string;
  className: string;
}) => {
  switch (type) {
    case 'h1':
      return <h1 className={className}>{text}</h1>;
    case 'h2':
      return <h2 className={className}>{text}</h2>;
    case 'h3':
      return <h3 className={className}>{text}</h3>;
    case 'h4':
      return <h4 className={className}>{text}</h4>;
    case 'h5':
      return <h5 className={className}>{text}</h5>;
    case 'h6':
      return <h6 className={className}>{text}</h6>;
    case 'p':
      return <p className={className}>{text}</p>;
    case 'span':
      return <span className={className}>{text}</span>;
    default:
      return <span className={className}>{text}</span>;
  }
};

export interface HeadingBlockProps {
  /** Small descriptive text that appears above the main title */
  eyebrow?: string;
  /** Highlighted text that appears between the title and the body */
  subheading?: string;
  /** Main heading text for the module */
  title?: string;
  /** Controls the size of the title text */
  titleType?: TextElementType;
  /** Controls the size of the title text */
  titleSize?: TitleSize;
  /** Rich text content for the body */
  bodyRichTextAsHTML?: string;
  /** Text alignment for the heading content */
  alignment?: HeadingAlignment;
  /** Array of call-to-action buttons */
  ctas?: CTAProps[];
  /* Caption Text*/
  caption?: string;
  /** Custom class name for the heading block */
  className?: string;
  /** Published at date from CMS */
  publishedAt?: string;
  /** Show inverse color scheme */
  showInverse?: boolean;
  /** Whether the title should be a gradient */
  titleGradient?: string;
}

/**
 * Common Header component for all modules
 * @param props - The props for the HeadingBlock component
 * @returns The HeadingBlock component
 */
export const HeadingBlock: React.FC<HeadingBlockProps> = ({
  eyebrow,
  subheading,
  title,
  titleType = 'h2',
  titleSize = TITLE_SIZE_OPTIONS.LARGE,
  bodyRichTextAsHTML,
  alignment = HEADING_ALIGNMENT_OPTIONS.CENTER,
  ctas,
  caption,
  className = '',
  showInverse = false,
  titleGradient,
}) => {
  const styleClassName = classNames(
    styles['heading-block'],
    styles[`heading-block__${titleSize}`],
    styles[`heading-block__${alignment}`],
    showInverse && styles.heading_block_inverse,
    className,
  );

  return (
    <div className={styleClassName}>
      <div className={styles.content}>
        {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
        {title &&
          (titleGradient ? (
            <GradientTypography
              title={title}
              as={titleType}
              gradient={titleGradient}
              className={styles[`heading-block__${alignment}`]}
            />
          ) : (
            <TextElement type={titleType} text={title} className={styles.title} />
          ))}
        {subheading && <span className={styles.subheading}>{subheading}</span>}
        {bodyRichTextAsHTML && (
          <div
            style={alignment === HEADING_ALIGNMENT_OPTIONS.CENTER ? { textAlign: 'center' } : {}}
            className={styles.body_rich_text}
            dangerouslySetInnerHTML={{ __html: bodyRichTextAsHTML }}
            suppressHydrationWarning={true}
          />
        )}
      </div>
      {ctas && ctas.length > 0 && (
        <CTABlock
          ctas={ctas}
          caption={caption}
          className={styles.heading_ctas}
          showInverse={showInverse}
        />
      )}
    </div>
  );
};
